import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Change } from '../../models';
import { Icon } from '../Icon';
import { Markdown } from '../Markdown';
import { Button } from '../Button';
import { FieldHelp } from '../FieldHelp';
import { RequesterEmails } from '../RequesterEmails';
import { EmailAuthBadges } from '../EmailAuthBadges';

interface TechnicalContextPanelProps {
    change: Change;
    onMergeReview?: () => void;
}

function TechnicalContextPanel(props: TechnicalContextPanelProps) {
    const { change, onMergeReview } = props;
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [showRawHeaders, setShowRawHeaders] = useState(false);
    const technicalContext = change.resolvedTechnicalContext();
    const intakeEntries = Change.intakeEntries(change.intake);
    const intakeHeaders = change.intake?.headers;

    if (!change.hasTechnicalContext())
        return null;

    const environmentEntries = Object.entries(technicalContext?.environment || {});
    const hasHeaderDetails = Boolean(
        intakeHeaders?.auth ||
        intakeHeaders?.from ||
        intakeHeaders?.replyTo ||
        intakeHeaders?.returnPath ||
        intakeHeaders?.xSpamStatus ||
        intakeHeaders?.xSpamScore ||
        intakeHeaders?.raw
    );

    return (
        <div className="col-span-full mt-2 border rounded-md overflow-hidden">
            <button
                type="button"
                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-900 ${open ? 'rounded-t-md' : 'rounded-md'}`}
                onClick={() => setOpen(!open)}
            >
                <span className="flex items-center gap-1">
                    <Icon type="notes" color="current" className="inline mr-1.5" />
                    {t('changes.technical_context')}
                    <FieldHelp content={t('changes.help.developer_notes') || ''} />
                </span>
                <Icon type={open ? 'up' : 'down'} color="current" className="inline" />
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-3 text-sm text-gray-800 dark:text-gray-100 border-t">
                    {intakeEntries.length > 0 && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_intake')}</div>
                            <dl className="mt-1 grid grid-cols-1 gap-1">
                                {intakeEntries.map(([key, value]) => (
                                    <div key={key} className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                            {t(`changes.technical_context_intake_${key}`)}
                                        </dt>
                                        <dd className="break-all">
                                            {key === 'senderEmail' ? (
                                                <RequesterEmails emails={[value]} empty={value} />
                                            ) : (
                                                value
                                            )}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}
                    {hasHeaderDetails && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_email_headers')}</div>
                            <div className="mt-2">
                                <EmailAuthBadges intake={change.intake} />
                            </div>
                            <dl className="mt-2 grid grid-cols-1 gap-1">
                                {intakeHeaders?.from && (
                                    <div className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                            {t('changes.technical_context_email_headers_from')}
                                        </dt>
                                        <dd className="break-all">{intakeHeaders.from}</dd>
                                    </div>
                                )}
                                {intakeHeaders?.replyTo && (
                                    <div className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                            {t('changes.technical_context_email_headers_reply_to')}
                                        </dt>
                                        <dd className="break-all">{intakeHeaders.replyTo}</dd>
                                    </div>
                                )}
                                {intakeHeaders?.returnPath && (
                                    <div className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                            {t('changes.technical_context_email_headers_return_path')}
                                        </dt>
                                        <dd className="break-all">{intakeHeaders.returnPath}</dd>
                                    </div>
                                )}
                                {intakeHeaders?.xSpamStatus && (
                                    <div className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                            {t('changes.technical_context_email_headers_x_spam_status')}
                                        </dt>
                                        <dd className="break-all">{intakeHeaders.xSpamStatus}</dd>
                                    </div>
                                )}
                                {intakeHeaders?.xSpamScore && (
                                    <div className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300 shrink-0">
                                            {t('changes.technical_context_email_headers_x_spam_score')}
                                        </dt>
                                        <dd className="break-all">{intakeHeaders.xSpamScore}</dd>
                                    </div>
                                )}
                            </dl>
                            {intakeHeaders?.raw && (
                                <div className="mt-2">
                                    <Button
                                        small
                                        color="default"
                                        icon={showRawHeaders ? 'up' : 'down'}
                                        title={
                                            (showRawHeaders
                                                ? t('changes.technical_context_email_headers_hide')
                                                : t('changes.technical_context_email_headers_show')) as string
                                        }
                                        onClick={() => setShowRawHeaders(!showRawHeaders)}
                                    />
                                    {showRawHeaders && (
                                        <pre className="mt-2 max-h-80 overflow-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2 text-xs font-mono whitespace-pre-wrap break-words text-gray-800 dark:text-gray-100">
                                            {intakeHeaders.raw}
                                        </pre>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {technicalContext?.sentry && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_sentry')}</div>
                            {technicalContext.sentry.culprit && (
                                <div className="mt-1 font-mono text-xs text-gray-700 dark:text-gray-200 break-all">
                                    {technicalContext.sentry.culprit}
                                </div>
                            )}
                            {technicalContext.sentry.metadata && (
                                <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded overflow-auto text-xs font-mono whitespace-pre-wrap break-words">
                                    {JSON.stringify(technicalContext.sentry.metadata, null, 2)}
                                </div>
                            )}
                        </div>
                    )}
                    {environmentEntries.length > 0 && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_environment')}</div>
                            <dl className="mt-1 grid grid-cols-1 gap-1">
                                {environmentEntries.map(([key, value]) => (
                                    <div key={key} className="flex gap-2">
                                        <dt className="font-medium text-gray-600 dark:text-gray-300">{key}</dt>
                                        <dd className="break-all">{String(value)}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}
                    {technicalContext?.mergeReview?.candidateId && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_merge_review')}</div>
                            <div className="mt-1">
                                {t('changes.technical_context_merge_review_detail', {
                                    slug: technicalContext.mergeReview.slug,
                                    confidence: technicalContext.mergeReview.confidence
                                })}
                            </div>
                            {onMergeReview && (
                                <div className="mt-2">
                                    <Button
                                        small
                                        color="primary"
                                        icon="merge"
                                        title={t('changes.technical_context_merge_review_cta', { slug: technicalContext.mergeReview.slug }) as string}
                                        onClick={onMergeReview}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {technicalContext?.triage && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_triage')}</div>
                            <div className="mt-1">
                                {technicalContext.triage.spamRejected
                                    ? t('changes.technical_context_spam_rejected')
                                    : null}
                                {technicalContext.triage.reason
                                    ? ` — ${technicalContext.triage.reason}`
                                    : null}
                            </div>
                        </div>
                    )}
                    {technicalContext?.legacyNotes?.trim() && (
                        <div>
                            <div className="font-medium mt-2">{t('changes.technical_context_legacy')}</div>
                            <div className="mt-1 prose prose-sm max-w-none">
                                <Markdown content={technicalContext.legacyNotes} />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TechnicalContextPanel;
